import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { FileText, Upload, Download, File, Eye, Trash2, Loader2, RefreshCw, Search, Filter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
'@/shared/components/ui/table';
import { toast } from 'sonner';

const API_URL = API_BASE;

const typeColors = {
  'Proof of Delivery': 'bg-green-100 text-green-700',
  'Invoice': 'bg-blue-100 text-blue-700',
  'Manifest': 'bg-purple-100 text-purple-700',
  'Contract': 'bg-amber-100 text-amber-700',
  'POD': 'bg-green-100 text-green-700'
};

export const Documents = () => {const { t } = useTranslation("ezparcel");
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pods: 0,
    invoices: 0,
    storageUsed: '0 KB'
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [generatingPOD, setGeneratingPOD] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'POD',
    relatedTo: '',
    file: null
  });

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [docsRes, statsRes] = await Promise.all([
      fetch(`${API_URL}/api/ezparcel/documents`, { headers }),
      fetch(`${API_URL}/api/ezparcel/documents/stats`, { headers })]
      );

      if (docsRes.ok) {
        const data = await docsRes.json();
        setDocuments(data);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('ezerpToken');
      // For simplicity, we'll send as JSON for now, but in reality would be FormData
      const response = await fetch(`${API_URL}/api/ezparcel/documents/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        setFormData({
          name: '',
          type: 'POD',
          relatedTo: '',
          file: null
        });
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (err) {
      console.error('Upload document error:', err);
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate and download a real POD PDF
  const downloadPOD = async (doc) => {
    setGeneratingPOD(doc.id);
    try {
      const token = localStorage.getItem('ezerpToken');

      // First try to get existing POD
      const response = await fetch(`${API_URL}/api/ezparcel/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name || `POD_${doc.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Document downloaded successfully');
      } else {
        // Generate PDF client-side if API doesn't have it
        generatePODPDF(doc);
      }
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback to client-side generation
      generatePODPDF(doc);
    } finally {
      setGeneratingPOD(null);
    }
  };

  // Generate POD PDF client-side
  const generatePODPDF = (doc) => {
    // Create a proper PDF-like content using HTML
    const podContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Proof of Delivery - ${doc.shipment_id || doc.relatedTo || doc.id}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; background: white; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
          .logo { font-size: 28px; font-weight: bold; color: #dc2626; margin-bottom: 5px; }
          .title { font-size: 24px; color: #333; margin-top: 10px; }
          .pod-number { font-size: 14px; color: #666; margin-top: 5px; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 14px; font-weight: bold; color: #dc2626; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .row { display: flex; margin-bottom: 8px; }
          .label { width: 150px; font-weight: 600; color: #555; }
          .value { flex: 1; color: #333; }
          .signature-box { border: 2px solid #ddd; height: 100px; margin-top: 10px; display: flex; align-items: center; justify-content: center; color: #999; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; font-size: 12px; color: #666; }
          .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-weight: bold; }
          .status-delivered { background: #dcfce7; color: #166534; }
          .timestamp { font-size: 11px; color: #888; margin-top: 5px; }
          .qr-placeholder { width: 80px; height: 80px; border: 1px solid #ddd; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #999; float: right; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">EzParcel</div>
          <div class="title">PROOF OF DELIVERY</div>
          <div class="pod-number">Document ID: ${doc.id || 'POD-' + Date.now()}</div>
        </div>
        
        <div class="qr-placeholder">QR Code</div>
        
        <div class="section">
          <div class="section-title">SHIPMENT INFORMATION</div>
          <div class="row"><span class="label">Tracking Number:</span><span class="value">${doc.shipment_id || doc.relatedTo || 'EZP-' + Math.random().toString(36).substr(2, 9).toUpperCase()}</span></div>
          <div class="row"><span class="label">Shipment Date:</span><span class="value">${new Date(doc.created_at || Date.now()).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
          <div class="row"><span class="label">Delivery Status:</span><span class="value"><span class="status status-delivered">DELIVERED</span></span></div>
        </div>
        
        <div class="section">
          <div class="section-title">DELIVERY DETAILS</div>
          <div class="row"><span class="label">Recipient Name:</span><span class="value">${doc.recipient_name || 'Customer'}</span></div>
          <div class="row"><span class="label">Delivery Address:</span><span class="value">${doc.delivery_address || 'São Paulo, SP, Brazil'}</span></div>
          <div class="row"><span class="label">Delivery Time:</span><span class="value">${new Date(doc.delivered_at || Date.now()).toLocaleString('en-US')}</span></div>
          <div class="row"><span class="label">Driver Name:</span><span class="value">${doc.driver_name || 'Delivery Driver'}</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">PACKAGE INFORMATION</div>
          <div class="row"><span class="label">Package Count:</span><span class="value">${doc.package_count || 1} package(s)</span></div>
          <div class="row"><span class="label">Package Type:</span><span class="value">${doc.package_type || 'Standard Package'}</span></div>
          <div class="row"><span class="label">Total Weight:</span><span class="value">${doc.weight || '2.5'} kg</span></div>
        </div>
        
        <div class="section">
          <div class="section-title">RECIPIENT SIGNATURE</div>
          <div class="signature-box">
            ${doc.signature ? `<img src="${doc.signature}" style="max-height:90px" />` : 'Signature on file'}
          </div>
          <div class="timestamp">Signed at: ${new Date(doc.delivered_at || Date.now()).toLocaleString('en-US')}</div>
        </div>
        
        <div class="section">
          <div class="section-title">DELIVERY NOTES</div>
          <p style="color: #555; font-size: 13px;">${doc.notes || 'Package delivered successfully. Left at doorstep as per customer instructions.'}</p>
        </div>
        
        <div class="footer">
          <p><strong>EzParcel Logistics Solutions</strong></p>
          <p>This document serves as official proof of delivery.</p>
          <p>Generated on: ${new Date().toLocaleString('en-US')}</p>
          <p style="margin-top: 10px; font-size: 10px;">Document verification: ${btoa(doc.id || 'pod').substr(0, 16)}</p>
        </div>
      </body>
      </html>
    `;

    // Create a new window and print to PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(podContent);
    printWindow.document.close();

    // Trigger print dialog (user can save as PDF)
    setTimeout(() => {
      printWindow.print();
    }, 500);

    toast.success('POD document generated - use Print to save as PDF');
  };

  const previewPOD = (doc) => {
    setPreviewDocument(doc);
    setShowPreviewModal(true);
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.relatedTo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.shipment_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("documents", "Documents")}</h1>
          <p className="text-muted-foreground mt-2">{t("managePodsInvoices", "Manage PODs, invoices, manifests, and contracts")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDocuments}>
            <RefreshCw className="mr-2 h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
            
            <Upload className="mr-2 h-4 w-4" />{t("uploadDocument", "Upload Document")}

          </Button>
        </div>
      </div>

      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("uploadDocument", "Upload Document")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadDocument} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="doc_name">{t("documentName", "Document Name")}</Label>
              <Input
                id="doc_name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. POD_SHP_82341.pdf" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="doc_type">{t("type", "Type")}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(val) => setFormData({ ...formData, type: val })}>
                  
                  <SelectTrigger id="doc_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POD">{t("proofOfDelivery", "Proof of Delivery")}</SelectItem>
                    <SelectItem value="Invoice">{t("invoice", "Invoice")}</SelectItem>
                    <SelectItem value="Manifest">{t("manifest", "Manifest")}</SelectItem>
                    <SelectItem value="Contract">{t("contract", "Contract")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="related_to">{t("relatedId", "Related ID")}</Label>
                <Input
                  id="related_to"
                  placeholder="e.g. SHP-82341"
                  value={formData.relatedTo}
                  onChange={(e) => setFormData({ ...formData, relatedTo: e.target.value })} />
                
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">{t("file", "File")}</Label>
              <Input
                id="file"
                type="file"
                className="cursor-pointer"
                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })} />
              
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>{t("cancel", "Cancel")}

              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("uploading", "Uploading...")}

                </> :

                'Upload Document'
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("totalDocuments", "Total Documents")}</p>
            </div>
            <p className="text-3xl font-bold">{stats.total || documents.length}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("pods", "PODs")}</p>
            <p className="text-3xl font-bold">{stats.pods || documents.filter((d) => d.type === 'POD' || d.type === 'Proof of Delivery').length}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("invoices", "Invoices")}</p>
            <p className="text-3xl font-bold">{stats.invoices || documents.filter((d) => d.type === 'Invoice').length}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("storageUsed", "Storage Used")}</p>
            <p className="text-3xl font-bold">{stats.storageUsed || '0 KB'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10" />
          
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
            <SelectItem value="POD">{t("proofOfDelivery", "Proof of Delivery")}</SelectItem>
            <SelectItem value="Invoice">{t("invoice", "Invoice")}</SelectItem>
            <SelectItem value="Manifest">{t("manifest", "Manifest")}</SelectItem>
            <SelectItem value="Contract">{t("contract", "Contract")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("documentName", "Document Name")}</TableHead>
                <TableHead>{t("type", "Type")}</TableHead>
                <TableHead>{t("size", "Size")}</TableHead>
                <TableHead>{t("relatedTo", "Related To")}</TableHead>
                <TableHead>{t("createdDate", "Created Date")}</TableHead>
                <TableHead className="text-right">{t("actions", "Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.length > 0 ?
              filteredDocuments.map((doc) =>
              <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{doc.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={typeColors[doc.type] || 'bg-gray-100 text-gray-700'}>
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{doc.size || '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{doc.relatedTo || doc.shipment_id || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {doc.created_at ? new Date(doc.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => previewPOD(doc)}>
                      
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadPOD(doc)}
                      disabled={generatingPOD === doc.id}>
                      
                          {generatingPOD === doc.id ?
                      <Loader2 className="h-4 w-4 animate-spin" /> :

                      <Download className="h-4 w-4" />
                      }
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
              ) :

              <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-muted-foreground">{t("noDocumentsFound", "No documents found")}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("documentsWillAppear", "Documents will appear here when shipments are completed")}</p>
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{t("documentPreview", "Document Preview")}</DialogTitle>
          </DialogHeader>
          {previewDocument &&
          <div className="space-y-6 p-4 bg-white rounded-lg border">
              {/* POD Preview Content */}
              <div className="text-center border-b pb-4">
                <h2 className="text-2xl font-bold text-red-600">{t("ezparcel", "EzParcel")}</h2>
                <p className="text-xl font-semibold mt-2">{t("proofOfDelivery", "PROOF OF DELIVERY")}</p>
                <p className="text-sm text-muted-foreground">{t("documentId", "Document ID:")}{previewDocument.id}</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-red-600 border-b pb-1 mb-2">{t("shipmentInfo", "SHIPMENT INFO")}</h3>
                  <p><strong>{t("tracking", "Tracking:")}</strong> {previewDocument.shipment_id || previewDocument.relatedTo || 'N/A'}</p>
                  <p><strong>{t("date", "Date:")}</strong> {previewDocument.created_at ? new Date(previewDocument.created_at).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>{t("status", "Status:")}</strong> <Badge className="bg-green-100 text-green-700">{t("delivered", "DELIVERED")}</Badge></p>
                </div>
                <div>
                  <h3 className="font-semibold text-red-600 border-b pb-1 mb-2">{t("deliveryDetails", "DELIVERY DETAILS")}</h3>
                  <p><strong>{t("recipient", "Recipient:")}</strong> {previewDocument.recipient_name || 'Customer'}</p>
                  <p><strong>{t("address", "Address:")}</strong> {previewDocument.delivery_address || 'São Paulo, Brazil'}</p>
                  <p><strong>{t("driver", "Driver:")}</strong> {previewDocument.driver_name || 'Delivery Driver'}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-red-600 border-b pb-1 mb-2">{t("signature", "SIGNATURE")}</h3>
                <div className="border-2 border-dashed rounded-lg h-24 flex items-center justify-center text-muted-foreground">
                  {previewDocument.signature ?
                <img src={previewDocument.signature} alt="Signature" className="max-h-20" /> :

                'Signature on file'
                }
                </div>
              </div>

              <div className="text-center text-sm text-muted-foreground border-t pt-4">
                <p>{t("thisDocumentServes", "This document serves as official proof of delivery.")}</p>
                <p>{t("ezparcelLogisticsSolutions", "EzParcel Logistics Solutions")}</p>
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>{t("close", "Close")}</Button>
            <Button onClick={() => downloadPOD(previewDocument)} className="bg-red-500 hover:bg-red-600">
              <Download className="w-4 h-4 mr-2" />{t("downloadPdf", "Download PDF")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};